import {AssistantVariant, DocumentProps, Message} from "../../models/models";
import {ConversationData} from "../../services/conversation.service";
import {ConversationDocumentInfo} from "../../models/conversation.models";

export interface ConversationState {
    conversationId: string | null;
    messages: Message[];
    streamingMessage: Message | null;
    answerVariants: AssistantVariant[];
    selectedGroupIds: number[];
    selectedDocIds: number[];
    selectedWholeCollectionIds: number[];
    selectedModelId: number | null;
    searchInternet: boolean;
    isGenerating: boolean;
    isLoading: boolean;
    documents: ConversationDocumentInfo[];
    contextExceeded: boolean;
}

export const initialState: ConversationState = {
    conversationId: null,
    messages: [],
    streamingMessage: null,
    answerVariants: [],
    selectedGroupIds: [],
    selectedDocIds: [],
    selectedWholeCollectionIds: [],
    selectedModelId: null,
    searchInternet: false,
    isGenerating: false,
    isLoading: false,
    documents: [],
    contextExceeded: false,
};

function updateLastUserMessage(messages: Message[], update: Partial<Message>): Message[] {
    const result = [...messages];
    for (let i = result.length - 1; i >= 0; i--) {
        if (!result[i].isSystem) {
            result[i] = { ...result[i], ...update };
            break;
        }
    }
    return result;
}

export type ConversationAction  =
    | { type: 'LOADING_STARTED' }
    | { type: 'LOADING_FINISHED'; payload: ConversationData }
    | { type: 'CONVERSATION_RESET' }

    | { type: 'MESSAGE_SENT';
        payload: {
            conversationId: string;
            userMessage: Message;
            loadingMessage: Message;
            searchInternet: boolean;
        }}
    | { type: 'RETRY_STARTED';
        payload: {
            conversationId: string;
            loadingMessage: Message;
            searchInternet: boolean;
        }}

    // streaming - every chunk is parsed by StreamingConversationManager
    | {
        type: 'CHUNK_RECEIVED';
        payload: {
            conversationId: string;
            cleanText: string;
            thinkingText: string;
            streamingText: string;
        };
    }
    | {
        type: 'STREAM_COMPLETED';
        payload: {
            conversationId: string;
            finalMessage: Message;
            answerVariants: AssistantVariant[];
            documents: ConversationDocumentInfo[];
            userMessageDocuments: DocumentProps[];
        };
    }
    | { type: 'STREAM_ERROR', payload?: { contextExceeded?: boolean} }

    | { type: 'GENERATING_STOPPED' }

    | { type: 'USER_MESSAGE_DOCUMENTS_UPDATED'; payload: Message['documents'] }

    | { type: 'COLLECTIONS_UPDATED'; payload: number[] }
    | { type: 'DOCUMENTS_UPDATED'; payload: number[] }
    | { type: 'WHOLE_COLLECTION_UPDATED'; payload: number[] }

    | { type: 'MODEL_SET'; payload: number }
    | { type: 'SEARCH_INTERNET_SET'; payload: boolean }

    | { type: 'VARIANTS_UPDATED'; payload: AssistantVariant[] }
    | { type: 'VARIANT_ACTIVATED'; payload: { variantId: number } };


export function conversationReducer(
    state: ConversationState,
    action: ConversationAction,
): ConversationState {
    switch (action.type) {
        case 'LOADING_STARTED': {
            return {
                ...state,
                isLoading: true,
                isGenerating: false,
                contextExceeded: false,
                streamingMessage: null,
                messages: []
            };
        }

        case "LOADING_FINISHED": {
            return {
                ...state,
                isLoading: false,
                isGenerating: false,
                contextExceeded: false,
                streamingMessage: null,
                conversationId: String(action.payload.id),
                messages: action.payload.messages,
                answerVariants: action.payload.answerVariants,
                selectedGroupIds: action.payload.selectedGroupIds,
                selectedDocIds: action.payload.selectedDocIds,
                selectedWholeCollectionIds: action.payload.selectedWholeCollectionIds,
                selectedModelId: action.payload.selectedModelId ?? state.selectedModelId,
                searchInternet: action.payload.searchInternet,
                documents: action.payload.documents,
            };
        }

        // reset conversation - keep settings: user, model, groups, search internet
        case 'CONVERSATION_RESET': {
            return {
                ...state,
                conversationId: null,
                messages: [],
                streamingMessage: null,
                answerVariants: [],
                documents: [],
                selectedGroupIds: [],
                selectedDocIds: [],
                selectedWholeCollectionIds: [],
                isLoading: false,
                isGenerating: false,
                contextExceeded: false,
            };
        }

        case 'MESSAGE_SENT': {
            const {conversationId, userMessage, loadingMessage, searchInternet} = action.payload;

            return {
                ...state,
                isGenerating: true,
                conversationId,
                messages: [...state.messages, userMessage],
                streamingMessage: loadingMessage,
                answerVariants: [],
                searchInternet,
            };
        }

        case 'RETRY_STARTED': {
            const { conversationId, loadingMessage, searchInternet } = action.payload;

            // delete the last assistant's response
            const messages = [...state.messages];
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].isSystem === true) {
                    messages.splice(i, 1);
                    break;
                }
            }

            return {
                ...state,
                conversationId,
                messages,
                streamingMessage: loadingMessage,
                answerVariants: [],
                isGenerating: true,
                searchInternet,
            };
        }

        case 'CHUNK_RECEIVED': {
            // guard: ignore chunk if it belongs to another conversation than the chosen one
            if (action.payload.conversationId !== state.conversationId) return state;
            if (!state.streamingMessage) return state;

            return {
                ...state,
                streamingMessage: {
                    ...state.streamingMessage,
                    text: action.payload.cleanText,
                    thinking: action.payload.thinkingText,
                    streamingText: action.payload.streamingText,
                    isLoading: false,
                },
            };
        }

        case 'STREAM_COMPLETED': {
            if (action.payload.conversationId !== state.conversationId) return state;

            const messages = updateLastUserMessage(state.messages, {
                documents: action.payload.userMessageDocuments,
            });

            return {
                ...state,
                messages: [...messages, action.payload.finalMessage],
                streamingMessage: null,
                answerVariants: action.payload.answerVariants,
                documents: action.payload.documents,
                isGenerating: false,
            };
        }

        case 'STREAM_ERROR': {
            return {
                ...state,
                isGenerating: false,
                contextExceeded: action.payload?.contextExceeded ?? state.contextExceeded,
                streamingMessage: state.streamingMessage ? {
                    ...state.streamingMessage,
                    isLoading: false,
                } : null,
            };
        }

        case 'GENERATING_STOPPED': {
            const currentStreamingMsg = state.streamingMessage;

            const hasText = currentStreamingMsg &&
                currentStreamingMsg.streamingText && currentStreamingMsg.streamingText.length > 0;
            return {
                ...state,
                isGenerating: false,
                messages: hasText
                    ? [...state.messages, { ...currentStreamingMsg, isLoading: false, streamingText: undefined }]
                    : state.messages,
                streamingMessage: null,
            };
        }

        case 'USER_MESSAGE_DOCUMENTS_UPDATED': {
            return {
                ...state,
                messages: updateLastUserMessage(state.messages, {
                    documents: action.payload,
                }),
            };
        }

        case 'COLLECTIONS_UPDATED': {
            const shouldKeepWholeCollections = state.selectedWholeCollectionIds.every(id =>
                action.payload.includes(id)
            );

            return {
                ...state,
                selectedGroupIds: action.payload,
                selectedWholeCollectionIds: shouldKeepWholeCollections ? state.selectedWholeCollectionIds : [],
            };
        }

        case 'DOCUMENTS_UPDATED': {
            return {...state, selectedDocIds: action.payload};
        }

        case 'WHOLE_COLLECTION_UPDATED': {
            return {...state, selectedWholeCollectionIds: action.payload};
        }

        case 'MODEL_SET': {
            return {...state, selectedModelId: action.payload};
        }

        case 'SEARCH_INTERNET_SET': {
            return {...state, searchInternet: action.payload};
        }

        case 'VARIANTS_UPDATED': {
            return {...state, answerVariants: action.payload};
        }

        case 'VARIANT_ACTIVATED': {
            const {variantId} = action.payload;
            const updatedVariants = state.answerVariants.map(variant => (
                {
                    ...variant,
                    active: variant.id === variantId,
                }
            ));

            const activeVariant = updatedVariants.find(variant => variant.active);

            if (!activeVariant) {
                return {
                    ...state,
                    answerVariants: updatedVariants,
                };
            }

            const messages = [...state.messages];
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].isSystem === true) {
                    messages[i] = {
                        ...messages[i],
                        text: activeVariant.content,
                        documents: activeVariant.documents,
                    };
                    break;
                }
            }

            return { ...state, answerVariants: updatedVariants, messages };
        }

        default:
            return state;
    }
}
